import { Connect, Message, SocketObservable } from '@genesislcap/foundation-comms';
import { observable } from '@microsoft/fast-element';
import { DI } from '@microsoft/fast-foundation';
import { AnalyticsPreferences, IVppa, IVppaInsert, IWatchlist } from '../types';
import { CASCADE_COMPANY_ID_WATCHLIST, CONTRACT_TYPE, WATCHLIST_ENTITY_TYPE } from '../utils';
import { FileUpload } from '../components/common/file-upload/file-upload';
import { ICompanyService } from './company.service';
import { updateProductTypeCriteriaByNonSEFParticipant } from '../utils/profiles';

export interface IVppaService {
  fileUpload: FileUpload;
  openVppaDetailModal(vppaId: string);
  closeVppaDetailModal();
  eventVppaInsert(vppa: IVppaInsert): Promise<any>;
  eventVppaUpdate(vppa: IVppa): Promise<any>;
  eventVppaCancel(vppaId: String, contractType: String): Promise<any>;
  sendVppaCsv(file: any, endpointUrl: any): Promise<any>;
  getAllVppas(CRITERIA_MATCH: string): SocketObservable<Message>;
  getMyVppas(CRITERIA_MATCH: string): SocketObservable<Message>;
  getProjectVppas(CRITERIA_MATCH: string): SocketObservable<Message>;
  getVppa(vppaId: string): Promise<Message<any>>;
  getVppas(projectId: string): Promise<Message<any>>;
  eventVppasInsert(vppaObjectsList): Promise<any>;
  eventInsertFavoritedVppa(entityId: string): Promise<any>;
  eventDeleteFavoritedVppa(entityId: string): Promise<any>;
  getWatchlistVppa(data): Promise<Message<any>>;
  getMarketDepthVppas(CRITERIA_MATCH: string): SocketObservable<Message>;
  getVppaHistoricalTs(vppaId): Promise<Message<any>>;
  getVppaForecastTs(vppaId): Promise<Message<any>>;
  getBuyerAnalyticsData(
    vppaIdList: string[],
    startDate: Date,
    endDate: Date,
    startDateHistorical: Date,
  ): Promise<Message<any>>;
  getVppaRanking(preferences: AnalyticsPreferences): Promise<Message<any>>;
  generateFile(vppaId: string): Promise<any>;
  fileDownload(data: any, fileName): void;
  getProjectKeyMetrics(vppaId: string): Promise<Message<any>>;
  getDashboardMetrics(CRITERIA_MATCH: string): SocketObservable<Message<any>>;
}

class VppaServiceImpl implements IVppaService {
  constructor(
    @Connect private connect: Connect,
    @ICompanyService private companyService: ICompanyService,
  ) {}
  public fileUpload: FileUpload = new FileUpload();

  @observable public vppaDetailModalVisibility = false;
  @observable public vppaDetailModalVppaId: string = null;

  public openVppaDetailModal(vppaId: string) {
    this.vppaDetailModalVppaId = vppaId;
    if (this.vppaDetailModalVppaId) {
      this.vppaDetailModalVisibility = true;
    }
  }

  public closeVppaDetailModal() {
    this.vppaDetailModalVppaId = null;
    this.vppaDetailModalVisibility = false;
  }

  async getWatchlistVppa(data): Promise<Message<any>> {
    return this.connect.request('WATCHLIST', {
      REQUEST: {
        WATCHLIST_ENTITY_ID: data.VPPA_ID,
      },
    });
  }

  public async eventVppaInsert(vppa: IVppaInsert): Promise<any> {
    if (!vppa) return;

    let eventName = 'EVENT_VPPA_INSERT';
    if (vppa.CONTRACT_TYPE === CONTRACT_TYPE.MINI_POST_BUILD_VPPA_AUTO_EXECUTED) {
      eventName = 'EVENT_VPPA_AUTO_EXECUTE_INSERT';
    }
    const request: any = await this.connect.commitEvent(eventName, {
      DETAILS: vppa,
      IGNORE_WARNINGS: true,
      VALIDATE: false,
    });

    return request;
  }

  public async eventInsertFavoritedVppa(entityId: string): Promise<any> {
    if (!entityId) return;
    const watchlist: IWatchlist = {
      CASCADE_COMPANY_ID: CASCADE_COMPANY_ID_WATCHLIST.CASCADE_COMPANY_ID,
      WATCHLIST_ENTITY_ID: entityId,
      WATCHLIST_ENTITY_ID_TYPE: WATCHLIST_ENTITY_TYPE.VPPA,
    };

    const request: any = await this.connect.commitEvent('EVENT_WATCHLIST_INSERT', {
      DETAILS: watchlist,
      IGNORE_WARNINGS: true,
      VALIDATE: false,
    });
    return request;
  }

  public async eventDeleteFavoritedVppa(entityId: string): Promise<any> {
    if (!entityId) return;
    const watchlist: IWatchlist = {
      CASCADE_COMPANY_ID: CASCADE_COMPANY_ID_WATCHLIST.CASCADE_COMPANY_ID,
      WATCHLIST_ENTITY_ID: entityId,
      WATCHLIST_ENTITY_ID_TYPE: WATCHLIST_ENTITY_TYPE.VPPA,
    };

    const request: any = await this.connect.commitEvent('EVENT_WATCHLIST_DELETE', {
      DETAILS: watchlist,
      IGNORE_WARNINGS: true,
      VALIDATE: false,
    });
    return request;
  }

  public async eventVppaUpdate(vppa: IVppa): Promise<any> {
    if (!vppa) return;

    let eventName = 'EVENT_VPPA_UPDATE';
    if (vppa.CONTRACT_TYPE === CONTRACT_TYPE.MINI_POST_BUILD_VPPA_AUTO_EXECUTED) {
      eventName = 'EVENT_VPPA_AUTO_EXECUTE_UPDATE';
    }

    const request: any = await this.connect.commitEvent(eventName, {
      DETAILS: vppa,
      IGNORE_WARNINGS: true,
      VALIDATE: false,
    });
    return request;
  }

  public async eventVppaCancel(vppaId: String, contractType: String): Promise<any> {
    if (!vppaId) return;
    let eventName = 'EVENT_VPPA_CANCEL';
    if (contractType === CONTRACT_TYPE.MINI_POST_BUILD_VPPA_AUTO_EXECUTED) {
      eventName = 'EVENT_VPPA_AUTO_EXECUTE_CANCEL';
    }
    const request: any = await this.connect.commitEvent(eventName, {
      DETAILS: vppaId,
      IGNORE_WARNINGS: true,
      VALIDATE: false,
    });
    return request;
  }

  getAllVppas(CRITERIA_MATCH: string): SocketObservable<Message<any>> {
    if (this.companyService.isNonSefParticipantAgreement()) {
      CRITERIA_MATCH = updateProductTypeCriteriaByNonSEFParticipant(CRITERIA_MATCH);
    }

    const params = {
      CRITERIA_MATCH,
    };
    const request: any = this.connect.stream(
      'ALL_PROJECT_VPPA',
      (streamMessage: Message) => {
        () => {};
      },
      (x) => {},
      params,
    );

    return request;
  }

  getMyVppas(CRITERIA_MATCH: string): SocketObservable<Message<any>> {
    if (this.companyService.isNonSefParticipantAgreement()) {
      CRITERIA_MATCH = updateProductTypeCriteriaByNonSEFParticipant(CRITERIA_MATCH);
    }

    const params = {
      CRITERIA_MATCH,
    };
    const request: any = this.connect.stream(
      'MY_PROJECT_VPPA',
      (streamMessage: Message) => {
        () => {};
      },
      (x) => {},
      params,
    );

    return request;
  }

  getProjectVppas(CRITERIA_MATCH: string): SocketObservable<Message<any>> {
    if (this.companyService.isNonSefParticipantAgreement()) {
      CRITERIA_MATCH = updateProductTypeCriteriaByNonSEFParticipant(CRITERIA_MATCH);
    }

    const params = {
      CRITERIA_MATCH,
    };
    const request: any = this.connect.stream(
        'INDIVIDUAL_PROJECT_VPPA',
        (streamMessage: Message) => {
          () => {};
        },
        (x) => {},
        params,
    );

    return request;
  }

  getVppa(vppaId: string): Promise<Message<any>> {
    return this.connect.request('VPPA_BY_ID', {
      REQUEST: {
        VPPA_ID: vppaId,
      },
    });
  }

  public getVppas(projectId: string): Promise<Message<any>> {
    return this.connect.request('VPPA', {
      REQUEST: {
        PROJECT_ID: projectId,
      },
    });
  }

  async sendVppaCsv(file: any, endpointUrl: any): Promise<any> {
    const headers = new Headers();
    headers.append('SESSION_AUTH_TOKEN', localStorage.getItem('authToken'));

    const formData = new FormData();
    formData.append(file.fileName, file);

    const settings = {
      method: 'POST',
      body: formData,
      headers: headers,
    };

    return new Promise((resolve, reject) => {
      try {
        resolve(
          fetch(endpointUrl, settings).then((fetchResponse) => {
            return fetchResponse.json();
          }),
        );
      } catch (error) {
        reject(error);
      }
    });
  }

  public async eventVppasInsert(vppaObjectsList): Promise<any> {
    if (!vppaObjectsList) return;

    const request: any = await this.connect.commitEvent('EVENT_VPPAS_INSERT', {
      DETAILS: { VPPAS_LIST: vppaObjectsList },
      IGNORE_WARNINGS: true,
      VALIDATE: false,
    });

    return request;
  }

  getMarketDepthVppas(CRITERIA_MATCH: string): SocketObservable<Message<any>> {
    if (this.companyService.isNonSefParticipantAgreement()) {
      CRITERIA_MATCH = updateProductTypeCriteriaByNonSEFParticipant(CRITERIA_MATCH, 'VPPA_');
    }

    const params = {
      CRITERIA_MATCH,
    };
    const request: any = this.connect.stream(
      'MARKET_DEPTH_VPPA',
      (streamMessage: Message) => {
        () => {};
      },
      (x) => {},
      params,
    );

    return request;
  }

  getVppaHistoricalTs(vppaId: any): Promise<Message<any>> {
    return this.connect.request('PROJECT_HISTORICAL_TS', {
      REQUEST: {
        VPPA_ID: vppaId,
      },
    });
  }

  getVppaForecastTs(vppaId: any): Promise<Message<any>> {
    return this.connect.request('PROJECT_FORECAST_TS', {
      REQUEST: {
        VPPA_ID: vppaId,
      },
    });
  }

  getBuyerAnalyticsData(
    vppaIdList: string[],
    startDate: Date,
    endDate: Date,
    startDateHistorical: Date,
  ): Promise<Message<any>> {
    return this.connect.request('BUYER_ANALYTICS_DATA', {
      REQUEST: {
        VPPA_ID_LIST: vppaIdList,
        START_DATE: startDate,
        END_DATE: endDate,
        START_DATE_HISTORICAL: startDateHistorical,
      },
    });
  }

  getVppaRanking(preferences: AnalyticsPreferences): Promise<Message<any>> {
    return this.connect.request('CONTRACT_RANKING_CALCULATION', {
      REQUEST: preferences,
    });
  }

  public async generateFile(vppaId: string): Promise<any> {
    const headers = new Headers();
    headers.append('SESSION_AUTH_TOKEN', localStorage.getItem('authToken'));

    const request = fetch('/sm/file-handler/key-metrics-download?vppa-id=' + vppaId, {
      method: 'GET',
      headers: headers,
    });
    return request;
  }

  public fileDownload(data: any, fileName) {
    const url = window.URL.createObjectURL(new Blob([data]));
    const a = document.createElement('a');
    a.setAttribute('href', url);
    a.setAttribute('download', fileName);
    a.click();
  }

  getProjectKeyMetrics(vppaId: string): Promise<Message<any>> {
    return this.connect.request('REQ_PROJECT_KEY_METRICS', {
      REQUEST: {
        VPPA_ID: vppaId,
      },
    });
  }

  getDashboardMetrics(CRITERIA_MATCH: string): SocketObservable<Message<any>> {
    const params = {
      CRITERIA_MATCH,
    };
    return this.connect.stream(
      'ALL_DASHBOARD_METRIC',
      (streamMessage: Message) => {
        () => {};
      },
      (x) => {},
      params,
    );
  }
}

export const IVppaService = DI.createInterface<IVppaService>((x) => x.singleton(VppaServiceImpl));
